import { z } from 'zod'

/**
 * Runtime config is loaded from /config.json at startup, so one build is promoted
 * unchanged through dev → staging → production (spec §13).
 */
const RuntimeConfigSchema = z.object({
  apiBaseUrl: z.string().min(1),
  auth: z
    .object({
      /** 'password': username/password form against the IFS backend; 'oidc': IdP redirect */
      mode: z.enum(['password', 'oidc']).default('password'),
    })
    .default({ mode: 'password' }),
  /** required when auth.mode is 'oidc' */
  oidc: z
    .object({
      authority: z.string().url(),
      clientId: z.string().min(1),
      scope: z.string().default('openid profile email'),
    })
    .optional(),
  sentryDsn: z.string().optional().default(''),
  environment: z.string().default('local'),
  idleTimeoutMinutes: z.number().positive().default(15),
  statusPollMs: z.number().positive().default(15_000),
})

export type RuntimeConfig = z.infer<typeof RuntimeConfigSchema>

let config: RuntimeConfig | null = null

export async function loadRuntimeConfig(url = '/config.json'): Promise<RuntimeConfig> {
  const res = await fetch(url, { cache: 'no-store' })
  if (!res.ok) throw new Error(`Failed to load ${url}: ${res.status}`)
  config = RuntimeConfigSchema.parse(await res.json())
  return config
}

export function setRuntimeConfig(
  value: Partial<RuntimeConfig> & Pick<RuntimeConfig, 'apiBaseUrl'>,
) {
  config = RuntimeConfigSchema.parse(value)
}

export function getConfig(): RuntimeConfig {
  if (!config) throw new Error('Runtime config not loaded. Call loadRuntimeConfig() first.')
  return config
}
